import { FC } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Grid,
  MultiSelect,
  Select,
  Stack,
  TextField,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { isValidSalutation, languages, salutations, useAuth } from '@/features/auth'
import { useLocalisation } from '@/utils/localisation'
import { error } from '@/utils/logger'

import { PersonalFormSchema, PersonalFormSchemaType } from './schema'
import { useUserUpdate } from '../api/useUserUpdate'

export const PersonalSettings: FC = () => {
  const { translate } = useLocalisation()
  const { user } = useAuth()
  const toaster = useToaster()
  const userMutation = useUserUpdate()
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PersonalFormSchemaType>({
    resolver: zodResolver(PersonalFormSchema),
    defaultValues: {
      salutation: user?.salutation && isValidSalutation(user?.salutation) ? user?.salutation : undefined,
      title: user?.title ?? undefined,
      firstName: user?.firstName,
      lastName: user?.lastName,
      phoneLandline: user?.phoneLandline ?? undefined,
      phoneMobile: user?.phoneMobile ?? undefined,
      languages: user?.languages ?? [],
    },
  })

  const onSubmit: SubmitHandler<PersonalFormSchemaType> = async (data) => {
    if (user) {
      await userMutation.mutateAsync({
        customerId: user.customer_id ?? '?',
        userId: user._id ?? '?',
        data,
      })
      toaster({
        title: translate('settings.personal.notifications.updated'),
      })
    } else {
      error('User tried to update but has no valid auth object')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>{translate('settings.personal.heading')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack gap={16}>
            <Grid col={2} gap={16}>
              <Select
                label={translate('settings.personal.fields.salutation.label')}
                placeholder={translate('settings.personal.fields.salutation.placeholder')}
                value={getValues('salutation')}
                onChange={(newSalutation) => {
                  if (isValidSalutation(newSalutation)) {
                    setValue('salutation', newSalutation, { shouldValidate: true })
                  }
                }}
                state={errors.salutation ? 'negative' : undefined}
                items={salutations.map((salutation) => ({
                  value: salutation,
                  label: translate(`salutations.${salutation}`),
                }))}
              />

              <TextField
                label={translate('settings.personal.fields.title.label')}
                placeholder={translate('settings.personal.fields.title.placeholder')}
                {...register('title')}
                state={errors.title ? 'negative' : undefined}
              />

              <TextField
                label={translate('settings.personal.fields.first_name.label')}
                placeholder={translate('settings.personal.fields.first_name.placeholder')}
                required
                {...register('firstName')}
                state={errors.firstName ? 'negative' : undefined}
              />

              <TextField
                label={translate('settings.personal.fields.last_name.label')}
                placeholder={translate('settings.personal.fields.last_name.placeholder')}
                required
                {...register('lastName')}
                state={errors.lastName ? 'negative' : undefined}
              />

              <TextField
                label={translate('settings.personal.fields.phone_landline.label')}
                placeholder={translate('settings.personal.fields.phone_landline.placeholder')}
                {...register('phoneLandline')}
                state={errors.phoneLandline ? 'negative' : undefined}
              />

              <TextField
                label={translate('settings.personal.fields.phone_mobile.label')}
                placeholder={translate('settings.personal.fields.phone_mobile.placeholder')}
                {...register('phoneMobile')}
                state={errors.phoneMobile ? 'negative' : undefined}
              />
            </Grid>

            <Controller
              control={control}
              name="languages"
              render={({ field: { onChange } }) => (
                <MultiSelect
                  items={languages.map((language) => ({
                    label: translate(`languages.${language}`),
                    value: language,
                  }))}
                  label={translate('settings.personal.fields.languages.label')}
                  placeholder={translate('settings.personal.fields.languages.placeholder')}
                  value={getValues('languages')}
                  onChange={(newLanguages) => {
                    onChange(newLanguages)
                  }}
                  state={errors.languages ? 'negative' : undefined}
                />
              )}
            />
          </Stack>
        </CardContent>
        <CardFooter>
          <Button type="submit">{translate('settings.personal.actions.update')}</Button>
        </CardFooter>
      </Card>
    </form>
  )
}
